import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Building2,
  FileText,
  FolderArchive,
  Leaf,
  Loader2,
  RefreshCw,
  Store,
} from "lucide-react";
import {
  CertificationButton,
  CertificationStandardHero,
} from "../../shared/ui";
import { getBibliotecaSummary } from "../api/biblioteca";
import type {
  BibliotecaStandard,
  BibliotecaSummary,
  BibliotecaTipo,
} from "../types/biblioteca";
import {
  bibliotecaParentRoutes,
  bibliotecaStandardLabels,
  bibliotecaTipoLabels,
  bibliotecaTipoRoute,
  bibliotecaTiposPorEstandar,
} from "../types/biblioteca";
import "../../standards/iso-27001-27701/styles/iso-27001-27701.css";
import "../../standards/iso-9001-14001/styles/iso-9001-14001.css";
import "../styles/biblioteca.css";

const initialSummary: BibliotecaSummary = {
  oficina: 0,
  medioambiente: 0,
  proveedores: 0,
};

const categoryIcons: Record<BibliotecaTipo, typeof Building2> = {
  oficina: Building2,
  medioambiente: Leaf,
  proveedores: Store,
};

type Props = {
  standard: BibliotecaStandard;
};

export function BibliotecaPage({ standard }: Props) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(initialSummary);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const categories = bibliotecaTiposPorEstandar[standard];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBibliotecaSummary(standard);
      setSummary(response.data);
    } catch {
      setError("No se pudo cargar el resumen de la Biblioteca.");
    } finally {
      setLoading(false);
    }
  }, [standard]);

  useEffect(() => {
    void load();
  }, [load]);

  const total = useMemo(
    () => categories.reduce((sum, type) => sum + summary[type], 0),
    [categories, summary],
  );

  return (
    <div className="page-shell biblioteca-page-shell">
      <section className="biblioteca-section-stack">
        <CertificationStandardHero
          title={`Biblioteca · ${bibliotecaStandardLabels[standard]}`}
          description="Repositorio de informes, certificados y documentación externa archivada por ámbito dentro de este estándar."
          secondaryDescription={
            standard === "iso-9001-14001"
              ? "Conserva Oficina, Medioambiente y Proveedores de la Biblioteca existente."
              : "Biblioteca independiente de seguridad y privacidad con Oficina y Proveedores."
          }
          onBack={() => void navigate({ to: bibliotecaParentRoutes[standard] })}
          backLabel={`Volver a ${bibliotecaStandardLabels[standard]}`}
          actions={
            <CertificationButton
              variant="secondary"
              icon={loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
              onClick={() => void load()}
              disabled={loading}
            >
              Actualizar
            </CertificationButton>
          }
          stats={[
            { icon: FolderArchive, label: "Categorías", value: categories.length, loading },
            { icon: FileText, label: "Documentos", value: total, loading },
          ]}
        />

        {error ? <div className="biblioteca-dialog-error" role="alert">{error}</div> : null}

        <section className="iso27001-data-card biblioteca-categories-card">
          <div className="iso27001-data-card-heading">
            <div>
              <div className="iso27001-section-eyebrow">Biblioteca documental</div>
              <h2>Categorías</h2>
              <p>Selecciona el ámbito documental que quieres consultar.</p>
            </div>
          </div>

          <div
            className={`biblioteca-category-grid-shared ${categories.length === 2 ? "biblioteca-category-grid-two" : ""}`}
          >
            {categories.map((type) => {
              const Icon = categoryIcons[type];
              return (
                <article key={type} className="biblioteca-category-card-shared">
                  <span className="biblioteca-category-icon-shared"><Icon size={22} /></span>
                  <div>
                    <h2>{bibliotecaTipoLabels[type]}</h2>
                    <p>{categoryDescription(type)}</p>
                  </div>
                  <div className="biblioteca-category-footer-shared">
                    <span><FileText size={15} /> {summary[type]} documentos</span>
                    <CertificationButton
                      variant="primary"
                      icon={<ArrowRight size={16} />}
                      onClick={() => void navigate({ to: bibliotecaTipoRoute(standard, type) })}
                    >
                      Abrir categoría
                    </CertificationButton>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </section>
    </div>
  );
}

function categoryDescription(type: BibliotecaTipo): string {
  if (type === "oficina") return "Informes y documentación vinculada a la actividad de oficina.";
  if (type === "medioambiente") return "Estudios, certificados e informes de carácter medioambiental.";
  return "Certificados, fichas e información documental de proveedores.";
}
